import React, {
  memo,
  useRef,
  useState,
  useCallback,
  useEffect,
  createContext,
  useContext,
} from "react";
import { View, StyleSheet, Animated } from "react-native";
import { useDrag, useDrop, useDragDropManager } from "react-dnd";
import {
  EXERCISE_DRAG_TYPE,
  useExerciseDrag,
} from "../../../contexts/ExerciseDragContext";
import { useToast } from "../../../contexts/ToastContext";
import {
  normalizeProtocolSlot,
  isCrossWeekBlocked,
  showBlockedMoveToast,
  clearBlockedMoveToast,
  calculateDropPosition,
  getDOMElement,
  executeMove,
  dropIndicatorStyles,
  DROP_INDICATOR_HEIGHT,
} from "./exerciseDragUtils";

// ============================================================================
// Global State
// ============================================================================

/**
 * Global map to track drop positions for each exercise during drag.
 * Key: `exercise-${workoutId}-${index}`
 * Value: "above" | "below" | null
 */
const dropPositionMap = new Map();

// ============================================================================
// Moved Exercise IDs Context
// ============================================================================

/**
 * Context to track which exercise IDs were just moved (for animation).
 * Only exercises in this set will animate when their position changes.
 */
const MovedExerciseIdsContext = createContext(new Set());

/**
 * Hook to access moved exercise IDs
 */
export const useMovedExerciseIds = () => useContext(MovedExerciseIdsContext);

// ============================================================================
// Drop Zone Components
// ============================================================================

/**
 * Drop zone between exercises for reordering.
 * Shows a drop indicator line when a dragged exercise is hovered over it.
 */
export const ExerciseDropZoneReactDnd = memo(
  ({ workoutId, workoutWeek, position, gap = 8 }) => {
    const indicatorOffset = Math.max(0, (gap - DROP_INDICATOR_HEIGHT) / 2);
    const containerRef = useRef(null);
    const { onMoveExercise, onMoveExerciseCrossWorkout, onMoveResult } =
      useMoveCallbacks();

    const [{ isOver, canDrop }] = useDrop({
      accept: EXERCISE_DRAG_TYPE,
      canDrop: (item) => {
        if (isCrossWeekBlocked(item, workoutWeek)) return false;
        return true;
      },
      drop: (item, monitor) => {
        if (monitor.didDrop()) return;

        const dropResult = {
          type: "exerciseDropZone",
          workoutId,
          workoutWeek,
          position,
        };

        const result = executeMove(
          item,
          dropResult,
          onMoveExercise,
          onMoveExerciseCrossWorkout,
          null
        );

        if (onMoveResult) {
          onMoveResult(result);
        }

        return dropResult;
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });

    const showIndicator = isOver && canDrop;

    return (
      <View ref={containerRef} style={{ height: gap, position: "relative" }}>
        <View
          style={[
            dropIndicatorStyles.dropIndicator,
            { top: indicatorOffset, opacity: showIndicator ? 1 : 0 },
          ]}
          pointerEvents="none"
        >
          <View
            style={[
              dropIndicatorStyles.dropIndicatorCircle,
              dropIndicatorStyles.dropIndicatorCircleLeft,
            ]}
          />
          <View
            style={[
              dropIndicatorStyles.dropIndicatorCircle,
              dropIndicatorStyles.dropIndicatorCircleRight,
            ]}
          />
        </View>
      </View>
    );
  }
);

/**
 * Trailing drop zone at the end of an exercise list.
 * Shows a drop indicator when dragging to the end of a workout.
 */
/**
 * Trailing drop zone for react-dnd implementation.
 *
 * Uses the same absolute positioning approach as dnd-kit:
 * - Wrapper fills the flex spacer (absolutely positioned, top: 0, bottom: 0)
 * - Hit area fills the wrapper (absolutely positioned, top: 0, bottom: 0)
 * - Only visible/active when there's actual space between exercises and footer
 * - No layout impact - spacer collapses to 0 if no space available
 */
export const TrailingDropZoneReactDnd = memo(
  ({ workoutId, workoutWeek, exerciseCount, gap = 8 }) => {
    const indicatorOffset = Math.max(0, (gap - DROP_INDICATOR_HEIGHT) / 2);
    const { showToast, hideToast } = useToast();
    const containerRef = useRef(null);
    const { onMoveExercise, onMoveExerciseCrossWorkout, onMoveResult } =
      useMoveCallbacks();

    const [{ isOver, canDrop, isProtocolBlocked }, drop] = useDrop({
      accept: EXERCISE_DRAG_TYPE,
      canDrop: (item) => {
        if (isCrossWeekBlocked(item, workoutWeek)) return false;
        if (item.workoutId === workoutId) {
          const endIndex = Math.max(0, exerciseCount - 1);
          // Allow dropping if it's not the last exercise (can reorder)
          // Or if there's only one exercise, allow dropping from other workouts
          return item.index !== endIndex;
        }
        return true;
      },
      drop: (item, monitor) => {
        if (monitor.didDrop()) return;

        const dropResult = {
          type: "trailingDropZone",
          workoutId,
          workoutWeek,
          position: exerciseCount,
        };

        const result = executeMove(
          item,
          dropResult,
          onMoveExercise,
          onMoveExerciseCrossWorkout,
          null
        );

        if (onMoveResult) {
          onMoveResult(result);
        }

        return dropResult;
      },
      collect: (monitor) => {
        const item = monitor.getItem();
        const isProtocolBlocked = item
          ? isCrossWeekBlocked(item, workoutWeek)
          : false;
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
          isProtocolBlocked,
        };
      },
    });

    // Attach drop connector to container
    drop(containerRef);

    const isBlocked = !canDrop;
    const showIndicator = isOver && canDrop;

    // Show blocked toast ONLY when hovering over blocked zone due to protocol slot restriction
    // Toast will remain visible until drag ends (cleared in drag end callback)
    // IMPORTANT: Only show toast for protocol slot blocking, not for other reasons (e.g., same workout, last exercise)
    useEffect(() => {
      if (isOver && isProtocolBlocked) {
        showBlockedMoveToast(showToast);
      }
      // Don't clear toast here - let it persist until drag ends
    }, [isOver, isProtocolBlocked, showToast]);

    return (
      <View style={dropIndicatorStyles.trailingDropWrapper}>
        <View
          ref={containerRef}
          style={dropIndicatorStyles.trailingDropHitArea}
        >
          <View
            style={[
              dropIndicatorStyles.dropIndicator,
              {
                top: indicatorOffset,
                left: 8,
                right: 8,
                opacity: showIndicator ? 1 : 0,
              },
            ]}
            pointerEvents="none"
          >
            <View
              style={[
                dropIndicatorStyles.dropIndicatorCircle,
                dropIndicatorStyles.dropIndicatorCircleLeft,
              ]}
            />
            <View
              style={[
                dropIndicatorStyles.dropIndicatorCircle,
                dropIndicatorStyles.dropIndicatorCircleRight,
              ]}
            />
          </View>
        </View>
      </View>
    );
  }
);

/**
 * Drop zone for empty workouts.
 * Shows a dashed border when a dragged exercise is hovered over an empty workout.
 */
export const EmptyWorkoutDropZoneReactDnd = memo(
  ({ workoutId, workoutWeek, children, style }) => {
    const { showToast, hideToast } = useToast();
    const containerRef = useRef(null);
    const { onMoveExerciseCrossWorkout, onMoveResult } = useMoveCallbacks();

    const [{ isOver, canDrop, isProtocolBlocked }, drop] = useDrop({
      accept: EXERCISE_DRAG_TYPE,
      canDrop: (item) => {
        if (isCrossWeekBlocked(item, workoutWeek)) return false;
        return item.workoutId !== workoutId; // Only cross-workout
      },
      drop: (item, monitor) => {
        if (monitor.didDrop()) return;

        const dropResult = {
          type: "emptyWorkoutDropZone",
          workoutId,
          workoutWeek,
          position: 0,
        };

        const result = executeMove(
          item,
          dropResult,
          null, // Empty workouts only support cross-workout moves
          onMoveExerciseCrossWorkout,
          null
        );

        if (onMoveResult) {
          onMoveResult(result);
        }

        return dropResult;
      },
      collect: (monitor) => {
        const item = monitor.getItem();
        const isProtocolBlocked = item
          ? isCrossWeekBlocked(item, workoutWeek)
          : false;
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop(),
          isProtocolBlocked,
        };
      },
    });

    // Attach drop connector to container
    drop(containerRef);

    const isBlocked = !canDrop;
    const showActive = isOver && canDrop;

    // Show blocked toast ONLY when hovering over blocked zone due to protocol slot restriction
    // Toast will remain visible until drag ends (cleared in drag end callback)
    // IMPORTANT: Only show toast for protocol slot blocking, not for other reasons (e.g., same workout)
    useEffect(() => {
      if (isOver && isProtocolBlocked) {
        showBlockedMoveToast(showToast);
      }
      // Don't clear toast here - let it persist until drag ends
    }, [isOver, isProtocolBlocked, showToast]);

    return (
      <View ref={containerRef} style={style}>
        <View
          style={[
            dropIndicatorStyles.dropZoneActive,
            {
              opacity: showActive ? 1 : 0,
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: "none",
            },
          ]}
        />
        {children}
      </View>
    );
  }
);

// ============================================================================
// Draggable Exercise Component
// ============================================================================

/**
 * Draggable exercise card wrapper using react-dnd.
 *
 * Makes an exercise card draggable and shows drop indicators (above/below)
 * based on pointer position when hovering during drag.
 *
 * Features:
 * - Uses default react-dnd drag preview (automatic preview from element)
 * - Visual feedback (opacity change when dragging)
 * - Drop position detection (top 50% = above, bottom 50% = below)
 * - Protocol slot week restriction validation
 */
export const DraggableExerciseReactDnd = memo(
  ({ exercise, idx, workoutId, workoutWeek, gap = 8, children }) => {
    const containerRef = useRef(null);
    const [dropPosition, setDropPosition] = useState(null);
    const { showToast, hideToast } = useToast();
    const movedExerciseIds = useMovedExerciseIds();
    const scaleAnim = useRef(new Animated.Value(1)).current;
    const prevMovedIdsRef = useRef(new Set());

    // Simple scale animation when exercise is moved
    useEffect(() => {
      const isMoved = movedExerciseIds.has(exercise?.id);
      const wasMoved = prevMovedIdsRef.current.has(exercise?.id);

      if (isMoved && !wasMoved) {
        Animated.sequence([
          Animated.timing(scaleAnim, {
            toValue: 1.02,
            duration: 150,
            useNativeDriver: true,
          }),
          Animated.timing(scaleAnim, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
          }),
        ]).start();
      }

      prevMovedIdsRef.current = new Set(movedExerciseIds);
    }, [exercise?.id, movedExerciseIds, scaleAnim]);

    // Calculate indicator offset
    const indicatorOffset = (gap + DROP_INDICATOR_HEIGHT) / 2;

    // Setup drag with auto-scroll integration
    const {
      startAutoScroll,
      stopAutoScroll,
      updateDragPosition,
      setActiveDragItem,
      clearActiveDragItem,
    } = useExerciseDrag();

    // Get move callbacks from context
    const { onMoveExercise, onMoveExerciseCrossWorkout, onMoveResult } =
      useMoveCallbacks();

    // Global pointer tracking for auto-scroll (works even when not hovering over drop targets)
    const isDraggingRef = useRef(false);

    // Setup drag with auto-scroll integration
    const [{ isDragging }, drag, dragPreview] = useDrag({
      type: EXERCISE_DRAG_TYPE,
      item: () => {
        const item = {
          type: EXERCISE_DRAG_TYPE,
          exerciseId: exercise?.id,
          workoutId,
          index: idx,
          protocolSlot: normalizeProtocolSlot(exercise?.protocolSlot),
          sourceWeek: workoutWeek,
        };

        // Start auto-scroll when drag begins
        startAutoScroll();
        setActiveDragItem(item);

        // Set cursor to "move" during drag
        if (typeof document !== "undefined" && document.body) {
          document.body.style.cursor = "move";
        }

        return item;
      },
      end: (item, monitor) => {
        // Stop auto-scroll when drag ends
        stopAutoScroll();
        clearActiveDragItem();
        clearBlockedMoveToast(hideToast);

        // Clear drop position map
        dropPositionMap.clear();

        // Reset cursor to default
        if (typeof document !== "undefined" && document.body) {
          document.body.style.cursor = "";
        }
      },
      collect: (monitor) => {
        // Track position continuously during drag for auto-scroll
        // This ensures auto-scroll works even when not hovering over drop targets
        // The global listeners above also track position,
        // but this provides an additional update source for reliability
        if (monitor.isDragging()) {
          const clientOffset = monitor.getClientOffset();
          if (
            clientOffset &&
            clientOffset.x !== undefined &&
            clientOffset.y !== undefined
          ) {
            // Update position for auto-scroll (throttled internally)
            // This complements the global pointer listeners
            updateDragPosition(clientOffset.x, clientOffset.y);
          }
        }

        return {
          isDragging: monitor.isDragging(),
        };
      },
    });

    // Get drag drop manager to access monitor for continuous position polling
    const dragDropManager = useDragDropManager();
    const monitor = dragDropManager.getMonitor();

    // Update ref when isDragging changes
    useEffect(() => {
      isDraggingRef.current = isDragging;
    }, [isDragging]);

    // Continuously poll monitor for position updates during drag
    // This ensures auto-scroll works even when not hovering over drop targets
    useEffect(() => {
      if (!isDragging) return;

      let rafId = null;
      const pollPosition = () => {
        if (!isDraggingRef.current) {
          if (rafId) cancelAnimationFrame(rafId);
          return;
        }

        // Get current position from react-dnd monitor
        const clientOffset = monitor.getClientOffset();
        if (
          clientOffset &&
          clientOffset.x !== undefined &&
          clientOffset.y !== undefined
        ) {
          // Update position for auto-scroll (throttled internally)
          updateDragPosition(clientOffset.x, clientOffset.y);
        }

        // Continue polling on next frame
        rafId = requestAnimationFrame(pollPosition);
      };

      // Start polling
      rafId = requestAnimationFrame(pollPosition);

      return () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    }, [isDragging, monitor, updateDragPosition]);

    // Create a ref callback for the drag handle
    // This properly extracts the DOM element from React Native Web refs
    const dragHandleRefCallback = useCallback(
      (node) => {
        if (node) {
          const domElement = getDOMElement(node);
          if (domElement) {
            drag(domElement);
          }
        } else {
          // Clear the drag connector when node is unmounted
          drag(null);
        }
      },
      [drag]
    );

    // Set up drag preview to use the entire ExerciseCard container
    // This makes the whole card appear as the drag preview, not just the drag handle
    useEffect(() => {
      if (containerRef.current && dragPreview) {
        const containerElement = getDOMElement(containerRef.current);
        if (containerElement) {
          dragPreview(containerElement);
        }
      }
    }, [dragPreview]);

    // Update preview when drag starts (isDragging becomes true)
    useEffect(() => {
      if (isDragging && containerRef.current && dragPreview) {
        const containerElement = getDOMElement(containerRef.current);
        if (containerElement) {
          dragPreview(containerElement);
        }
      }
    }, [isDragging, dragPreview]);

    // Setup drop with hover callback for drop position calculation
    const [{ isOver, canDrop }, drop] = useDrop({
      accept: EXERCISE_DRAG_TYPE,
      canDrop: (item) => {
        // Don't allow dropping on itself
        if (item.workoutId === workoutId && item.index === idx) return false;
        // Check protocol slot restrictions
        if (isCrossWeekBlocked(item, workoutWeek)) return false;
        return true;
      },
      hover: (item, monitor) => {
        if (!containerRef.current) return;

        // Don't show indicator on the item being dragged
        if (item.workoutId === workoutId && item.index === idx) {
          setDropPosition(null);
          return;
        }

        // Check if blocked
        if (isCrossWeekBlocked(item, workoutWeek)) {
          setDropPosition(null);
          showBlockedMoveToast(showToast);
          return;
        }

        // Get pointer position from monitor and update auto-scroll
        const clientOffset = monitor.getClientOffset();
        if (clientOffset) {
          updateDragPosition(clientOffset.x, clientOffset.y);
        }

        if (!clientOffset) return;

        // Get element rect
        const element = getDOMElement(containerRef.current);
        if (!element || typeof element.getBoundingClientRect !== "function")
          return;

        const rect = element.getBoundingClientRect();

        // Calculate drop position (top 50% = above, bottom 50% = below)
        const newDropPosition = calculateDropPosition(clientOffset.y, rect);

        if (newDropPosition) {
          setDropPosition(newDropPosition);

          // Store in global map for drop callback to access
          const mapKey = `exercise-${workoutId}-${idx}`;
          dropPositionMap.set(mapKey, newDropPosition);
        }
      },
      drop: (item, monitor) => {
        if (monitor.didDrop()) return;

        const dropPosition = dropPositionMap.get(
          `exercise-${workoutId}-${idx}`
        );

        const dropResult = {
          type: "exerciseTarget",
          workoutId,
          workoutWeek,
          index: idx,
          exerciseId: exercise?.id,
        };

        const result = executeMove(
          item,
          dropResult,
          onMoveExercise,
          onMoveExerciseCrossWorkout,
          dropPosition
        );

        if (onMoveResult) {
          onMoveResult(result);
        }

        return dropResult;
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });

    // Attach drop to container
    drop(containerRef);

    // Clear drop position when not hovering
    useEffect(() => {
      if (!isOver) {
        setDropPosition(null);
        const mapKey = `exercise-${workoutId}-${idx}`;
        dropPositionMap.delete(mapKey);
        // Don't clear blocked toast here - let it persist until drag ends
      }
    }, [isOver, workoutId, idx]);

    // Cleanup: Reset cursor if component unmounts during drag
    useEffect(() => {
      return () => {
        if (isDragging && typeof document !== "undefined" && document.body) {
          document.body.style.cursor = "";
        }
      };
    }, [isDragging]);

    // Check if we should show drop indicator
    const showDropIndicator = isOver && canDrop && dropPosition && !isDragging;

    return (
      <Animated.View
        ref={containerRef}
        style={[
          { position: "relative" },
          // Hide the original card completely when dragging (opacity 0)
          // The drag preview will show the entire card being moved
          isDragging && { opacity: 0 },
          !isDragging && {
            transform: [{ scale: scaleAnim }],
          },
        ]}
      >
        {/* Drop indicator - above */}
        {dropPosition === "above" && (
          <View
            style={[
              dropIndicatorStyles.dropIndicator,
              { top: -indicatorOffset, opacity: showDropIndicator ? 1 : 0 },
            ]}
            pointerEvents="none"
          >
            <View
              style={[
                dropIndicatorStyles.dropIndicatorCircle,
                dropIndicatorStyles.dropIndicatorCircleLeft,
              ]}
            />
            <View
              style={[
                dropIndicatorStyles.dropIndicatorCircle,
                dropIndicatorStyles.dropIndicatorCircleRight,
              ]}
            />
          </View>
        )}

        {/* The actual exercise card */}
        {typeof children === "function"
          ? children({
              dragListeners: {},
              dragHandleRef: dragHandleRefCallback,
              isDragging,
            })
          : children}

        {/* Drop indicator - below */}
        {dropPosition === "below" && (
          <View
            style={[
              dropIndicatorStyles.dropIndicator,
              { bottom: -indicatorOffset, opacity: showDropIndicator ? 1 : 0 },
            ]}
            pointerEvents="none"
          >
            <View
              style={[
                dropIndicatorStyles.dropIndicatorCircle,
                dropIndicatorStyles.dropIndicatorCircleLeft,
              ]}
            />
            <View
              style={[
                dropIndicatorStyles.dropIndicatorCircle,
                dropIndicatorStyles.dropIndicatorCircleRight,
              ]}
            />
          </View>
        )}
      </Animated.View>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.exercise?.id !== nextProps.exercise?.id) return false;
    if (prevProps.idx !== nextProps.idx) return false;
    if (prevProps.workoutId !== nextProps.workoutId) return false;
    if (prevProps.gap !== nextProps.gap) return false;
    if (prevProps.isEditMode !== nextProps.isEditMode) return false;
    return true;
  }
);

// ============================================================================
// Exercise DnD Provider
// ============================================================================

/**
 * Context to pass move callbacks to drop zones
 */
const MoveCallbacksContext = createContext({
  onMoveExercise: null,
  onMoveExerciseCrossWorkout: null,
  onMoveResult: null,
});

/**
 * Hook to access move callbacks
 */
export const useMoveCallbacks = () => useContext(MoveCallbacksContext);

/**
 * Exercise Drag & Drop Context Provider for react-dnd.
 *
 * react-dnd already has global DndProvider in _layout.js, so this is just
 * a context provider for moved exercise IDs and move callbacks.
 *
 * Features:
 * - Animation tracking for moved exercises only
 * - Provides move callbacks to drop zones via context
 *
 * @param {Function} onMoveExercise - Callback for same-workout reordering
 * @param {Function} onMoveExerciseCrossWorkout - Callback for cross-workout moves
 */
export const ExerciseDndProviderReactDnd = ({
  children,
  onMoveExercise,
  onMoveExerciseCrossWorkout,
}) => {
  const [movedExerciseIds, setMovedExerciseIds] = useState(new Set());

  // Handle move result from drop zones
  const handleMoveResult = useCallback((result) => {
    if (result) {
      const movedIds =
        Array.isArray(result) && result.length > 0
          ? new Set(result)
          : new Set([result]);
      setMovedExerciseIds(movedIds);
      // Clear after animation completes
      setTimeout(() => setMovedExerciseIds(new Set()), 400);
    }
  }, []);

  return (
    <MovedExerciseIdsContext.Provider value={movedExerciseIds}>
      <MoveCallbacksContext.Provider
        value={{
          onMoveExercise,
          onMoveExerciseCrossWorkout,
          onMoveResult: handleMoveResult,
        }}
      >
        {children}
      </MoveCallbacksContext.Provider>
    </MovedExerciseIdsContext.Provider>
  );
};

export default ExerciseDndProviderReactDnd;
